import { Container } from '@pixi/display';
import gsap from 'gsap';
import { Sprite } from 'pixi.js';

import { App, Shared } from '../../App';
import { AnalyticsEvent } from '../../plugins/AnalyticsPlugin';
import { NineSliceButton } from './NineSliceButton';

const defaultOpts = {
    includeHome: false,
    settingsIconBgAsset: 'button-ninesliced-yellow',
    muteIconBgAsset: 'button-ninesliced-yellow',
    homeIconBgAsset: 'button-ninesliced-yellow',
    expandDuration: 0.25,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    callBack: (enabled: boolean) => { /* */ },
};

export type SettingsButtonOpts = typeof defaultOpts;

export class SettingsButton
{
    public view: Container;

    public settingsButton!: NineSliceButton;
    public muteButton!: NineSliceButton;
    public homeButton?: NineSliceButton;
    public restartButton!: NineSliceButton;

    private _expanded: boolean;
    private _expandDuration: number;
    private _tween: gsap.core.Tween | undefined;
    private _positionMultiplier: number;
    private _muteIcon!: Sprite;

    constructor(app: App, opts: Partial<SettingsButtonOpts> = {})
    {
        const o = { ...defaultOpts, ...opts };

        this.view = new Container();
        this._expanded = false;
        this._expandDuration = o.expandDuration;
        this._positionMultiplier = 0;

        this._tween = undefined;

        const muteIcon = Sprite.from(Shared.assets.getTexture('icon-sound-on'));
        const homeIcon = Sprite.from(Shared.assets.getTexture('icon-home'));
        const restartIcon = Sprite.from(Shared.assets.getTexture('icon-replay'));

        if (o.includeHome)
        {
            this.homeButton = new NineSliceButton({
                icon: homeIcon,
                bgAsset: o.muteIconBgAsset,
                accessibleTitle: '',
                w: muteIcon.width * 1.5,
                h: muteIcon.height * 1.5,
            });
            this.view.addChild(this.homeButton.view);
            this.homeButton.view.y = ((muteIcon.height * 3) + 20) * this._positionMultiplier;
            this.homeButton.onPress.connect(() =>
            {
                app.controller.toHome();
                Shared.game.idle();
                this.setExpandTarget(0, false);
            });
            this.homeButton.enabled = false;

            this.restartButton = new NineSliceButton({
                icon: restartIcon,
                bgAsset: o.muteIconBgAsset,
                accessibleTitle: '',
                w: muteIcon.width * 1.5,
                h: muteIcon.height * 1.5,
            });
            this.view.addChild(this.restartButton.view);
            this.restartButton.view.y = ((muteIcon.height * 4) + 20) * this._positionMultiplier;
            this.restartButton.onPress.connect(() =>
            {
                this.setExpandTarget(0, false);
                app.controller.toPreRace();
            });
            this.restartButton.enabled = false;
        }

        this.muteButton = new NineSliceButton({
            icon: muteIcon,
            bgAsset: o.muteIconBgAsset,
            accessibleTitle: '',
            w: muteIcon.width * 1.5,
            h: muteIcon.height * 1.5,
        });
        this.view.addChild(this.muteButton.view);
        this.muteButton.view.y = ((muteIcon.height * 1.5) + 10) * this._positionMultiplier;
        this._muteIcon = muteIcon;
        this.muteButton.onPress.connect(() =>
        {
            Shared.settings.toggleMuted();
        });
        Shared.settings.muteChanged.connect((state) =>
        {
            muteIcon.texture = Shared.assets.getTexture(state ? 'icon-sound-off' : 'icon-sound-on');
        });
        this.muteButton.enabled = false;
        muteIcon.texture = Shared.assets.getTexture(Shared.settings.muted ? 'icon-sound-off' : 'icon-sound-on');

        const settingsIcon = Sprite.from(Shared.assets.getTexture('icon-pause'));

        this.settingsButton = new NineSliceButton({
            icon: settingsIcon,
            bgAsset: o.settingsIconBgAsset,
            accessibleTitle: '',
            w: settingsIcon.width * 1.5,
            h: settingsIcon.height * 1.5,
        });
        this.view.addChild(this.settingsButton.view);
        this.settingsButton.onPress.connect(() =>
        {
            this._expanded ? this.close(o.callBack) : this.open(o.callBack);
        });

        this.updateMainButtonIcon(false);
    }

    public setExpandTarget(val: number, expanded: boolean): void
    {
        if (this._tween) return;

        this.updateMainButtonIcon(expanded);
        this._tween = gsap.to(this, {
            _positionMultiplier: val,
            duration: this._expandDuration,
            onUpdate: () =>
            {
                if (this.homeButton)
                {
                    this.homeButton.view.y = ((this._muteIcon.height * 3) + 20) * this._positionMultiplier;
                }
                if (this.restartButton)
                {
                    this.restartButton.view.y = ((this._muteIcon.height * 4.5) + 30) * this._positionMultiplier;
                }
                this.muteButton.view.y = ((this._muteIcon.height * 1.5) + 10) * this._positionMultiplier;
            },
            onComplete: () =>
            {
                this._expanded = expanded;
                this._tween = undefined;

                this.muteButton.enabled = expanded;
                if (this.homeButton) this.homeButton.enabled = expanded;
                if (this.restartButton) this.restartButton.enabled = expanded;
            },
        });
    }

    public close(callBack?: (o: boolean)=> void): void
    {
        Shared.game.app.analytics.trackEvent(AnalyticsEvent.GAME_PLAY);
        Shared.game.resume();
        Shared.game.soundSystem.onResume();
        this.setExpandTarget(0, false);
        callBack?.(false);
    }

    public open(callBack?: (o: boolean)=> void): void
    {
        if(Shared.user.data.race == 0)
        {
            Shared.game.app.analytics.trackEvent(AnalyticsEvent.TUTORIAL_PAUSE);
        }
        else
        {
            Shared.game.app.analytics.trackEvent(AnalyticsEvent.GAME_PAUSE);
        }
        
        Shared.game.pause();
        Shared.game.soundSystem.onPause();
        this.setExpandTarget(1, true);
        callBack?.(true);
    }

    private updateMainButtonIcon(expanded: boolean): void
    {
        if (!expanded)
        {
            this.settingsButton.icon.texture = Shared.assets.getTexture('icon-pause');
        }
        else
        {
            this.settingsButton.icon.texture = Shared.assets.getTexture('icon-play');
        }
    }
}
